// Tipurile Pasului / zonei «pas19» — desprinse din `tipuri.ts`; orice importator
// le ia tot din `./tipuri` (bariera re-exportă fiecare fișier de aici).

import type { Ancora } from "../legaturi";
import { t } from "../../i18n";
import type { ProgresDetaliu } from "./pasi";

// --- Pasul 19: soluțiile proiectului --------------------------------------

export interface RezultatRamura {
  verdict?: string;
  nr_picate?: number;
  picate_pe_tipuri?: Record<string, number>;
  cost_structura_eur?: number | null;
  eroare?: string;
  /** Severitățile stării + SCORUL într-un singur număr (`solver.severitati`).
   *  Scorul e o PENALIZARE: mai mic e mai bun, 0 = nicio verificare picată.
   *  Ponderi 30 / 15 / 5 / 1 pe critical / high / med / low, iar fiecare
   *  verificare picată intră cu (deficit/100)^2,32 — deci una ratată la
   *  mustață aproape nu costă, una ratată complet costă exact ponderea ei.
   *  `nr_globale` = globalele BLOCANTE (critical + high, fără separarea
   *  perioadelor), numărate o singură dată pe server. */
  severitate?: {
    critical?: number; high?: number; med?: number; low?: number;
    nr_critical?: number; nr_high?: number; nr_med?: number; nr_low?: number;
    nr_globale?: number;
    critical_lista?: number[]; high_lista?: number[];
    med_lista?: number[]; low_lista?: number[];
    scor?: {
      total: number; critical: number; high: number; med: number; low: number;
      ponderi?: Record<string, number>; exponent?: number;
    };
  };
  // câmpurile noi ale ramurilor cu directive cardinale —
  // TOATE opționale, ca rulările vechi să se afișeze neschimbate
  dt12_pct?: number; // separarea ΔT₁₂ măsurată (%)
  extra_m?: number; // metri totali de stâlpi adăugați de umplere
  puritate_min_pct?: number; // puritatea modală minimă (încercările de torsiune)
  // distanța agregată față de gate-ul modal (0 = toate cele 4 verificări
  // modale trec; mai mare = mai departe de țintă) — opțional, rulările
  // vechi nu-l au
  scor_modal?: number;
  // breakpoint-urile GEOMETRICE ale Pasului 4 (istoricul de soluționare, `?bp=`): câte
  // elemente are propunerea și JURNALUL pașilor deterministe de reparare —
  // `aplicate` = câte elemente a schimbat pasul, `refuzate` = câte a oprit
  // verificarea de amplasare, `detalii` = lista element cu element
  nr_stalpi?: number;
  nr_grinzi?: number;
  // numele pasului pe care îl ADAUGĂ starea asta (restul sunt moșteniți de la
  // stările de sub ea) — se evidențiază în tabel
  pas_propriu?: string;
  // cât a schimbat pasul PROPRIU: elemente atinse și elemente pe care le-a
  // oprit verificarea de amplasare (pasul 1 e punctul de plecare, deci nu are
  // nici jurnal, nici cifre)
  aplicate?: number;
  refuzate?: number;
  pasi?: {
    solutie: number;
    nume: string;
    aplicate: number;
    refuzate?: number;
    detalii?: (string | { detaliu?: string })[];
  }[];
}

// RAPORTUL Pasului 19 și piesele lui, așa cum le scrie serverul
// (`analysis.analiza_solutiei`, `optimizare.stare_finala_compacta`).
export type CostCompact = {
  moneda?: string;
  total_direct_eur?: number | null;
  total_eur?: number | null;
  eur_pe_m2?: number | null;
  arie_desfasurata_m2?: number | null;
  total_material_eur?: number | null;
  total_manopera_eur?: number | null;
  preset_tarife?: string | null;
  precizie?: string | null;
  indicatori?: {
    kg_pe_m3?: number; cofraj_m2_pe_m3?: number; in_banda?: boolean;
    beton_m3?: number; otel_kg?: number; cofraj_m2?: number;
  } | null;
  articole?: {
    capitol?: string;
    articol: string;
    um?: string;
    cantitate?: number;
    total_eur: number;
    precizie?: string;
  }[];
  otel_kg?: number | null;
  otel_pe_diametru?: Record<string, { kg: number; n: number }> | null;
  /** aceeași armătură pe MĂRCI: formă + Ø + lungime croită */
  otel_pe_marca?: Record<string, { kg: number; n: number }> | null;
  // forma de dinainte de (deviz în lei, pe rețete): se recunoaște
  // ca să nu pară un deviz gol pe rezultatele salvate atunci
  total_lei?: number | null;
} | null;

export type RandModificare = {
  cheie: string;
  verificare?: string;
  element?: string;
  RE?: number | null;
  severitate_pct?: number;
  nr_puncte?: number;
  abordare?: number | null;
  abordari_total?: number;
  algoritm?: string;
  pseudocod?: string[];
  sarite?: string[] | null;
  modificare?: string;
  stare?: string; // aplicata | epuizata | acoperita | refuzata
  // op-urile CHIAR aplicate în overlay și reglajele schimbate. Erau în
  // răspunsul serverului dar nu se vedeau nicăieri, deci „ce s-a modificat"
  // rămânea o propoziție de proză, fără cifrele ei.
  ops?: Record<string, unknown>[] | null;
  reglaje?: Record<string, number | string | boolean> | null;
};

// un rând din tabloul de acoperire (`optimizare.colecteaza_stare`)
export type RandAcoperire = {
  pas: string;
  nume: string;
  citit: boolean;
  nr_verificari: number;
  nr_picate?: number;
  nr_la_limita?: number;
};

// O verificare PICATĂ, așa cum o salvează serverul în raport
// (`optimizare.stare_finala_compacta`). `ancora` spune UNDE se vede — pasul
// care a emis-o plus referința elementului pe pagina lui —, iar `descriere`
// spune LA CE se referă, fără să deschizi pasul.
export type VerificarePicata = {
  pas?: string;
  tip?: string;
  eticheta?: string;
  verificare?: string;
  element?: string;
  nivel?: number | string | null;
  directie?: string | null;
  RE?: number | null;
  prioritate?: string | null;
  descriere?: string;
  ancora?: Ancora | null;
};

// pașii lanțului care NU emit verdicte, declarați explicit pe server
// (`optimizare.SURSE_FARA_VERDICTE`). Fără ei, „N din M pași" nu se putea
// verifica: nu se vedea că M e o listă enumerată, nu tot lanțul.
export type PasFaraVerdicte = { pas: string; de_ce: string };

export type StarePicate = {
  nr_picate?: number | null;
  metrici?: {
    grinda_in_afara_peretilor?: {
      metri: number;
      niveluri_exceptate?: string[];
      pe_nivel?: { nivel: number; nume: string; metri: number;
        exceptat: boolean }[];
    };
  } | null;
  picate_pe_tipuri?: Record<string, number> | null;
  // lista propriu-zisă, pe care interfața o desfășoară sub fiecare categorie
  picate?: VerificarePicata[] | null;
  acoperire?: RandAcoperire[] | null;
  pasi_fara_verdicte?: PasFaraVerdicte[] | null;
  cost?: CostCompact;
  nota?: string;
};

export type RaportSolutie = {
  /** "draft" (mesh grosier) / "final" (rezoluția cu care se predă) */
  fidelitate?: string;
  rezolutie?: { h_mesh_m?: number; h_mesh_baza_m?: number };
  durata_min?: number;
  /** ce a hotărât analiza: trecerea de fază, bias-ul de rezoluție, epuizarea */
  note?: string[] | null;
  /** ce propune pentru pasul următor. Fidelitatea («probă/plină») nu se mai
   *  scrie: rețeaua se deduce din faza rândului (contract §1). */
  recomandari?: { key?: string; label?: string; reason?: string }[] | null;
  plecare?: (StarePicate & { solutie?: string | null }) | null;
  modificari?: RandModificare[];
  amanate?: { verificare?: string; element?: string;
    severitate_pct?: number; motiv?: string }[];
  netratate?: { verificare?: string; tip?: string; element?: string;
    motiv?: string }[];
  // confruntarea predicțiilor soluției precedente cu starea de acum: e
  // mecanismul prin care solverul se auto-corectează (o pârghie care nu
  // livrează de două ori se demotează), deci trebuie să se VADĂ
  registru_raport?: { cheie?: string; parghie?: string; promis?: number;
    livrat?: number; verdict?: string; demotata?: boolean }[] | null;
  nota_plan?: string | null;
  avertismente_pas4?: string[] | null;
  avertismente_pas4_total?: number | null;
  final?: StarePicate | null;
};


// REZULTATUL Pasului 19 (`analysis.analiza_solutiei`): verdictul și scorul
// soluției, raportul ei, ce a schimbat bucla externă și — când nu mai propune
// nimic — DE CE s-a oprit, în cuvintele structuristului (`stuck_reason`,
// numai în detaliu). `recomandari_geometrie` sunt planurile pe care detaliul
// le-ar fi aplicat geometriei și n-are voie (D15): se arată omului, nu pornesc
// niciun job. Cheile necitite de ecran rămân `unknown`, declarate ca să se
// vadă că există.
export interface RezultatPasul19 {
  verdict?: string;
  scor?: number | null;
  scor_parti?: Record<string, number> | null;
  raport_solutie?: RaportSolutie;
  stare_finala?: {
    nr_picate: number;
    rezumat?: Record<string, number>;
    picate?: { pas: string; tip: string; verificare: string;
      element: string; RE: number | null }[];
  };
  severitate?: RezultatRamura["severitate"];
  metrici_high?: Record<string, unknown>;
  cost_structura?: Record<string, unknown> | null;
  overlay_final?: Record<string, unknown> | null;
  clase?: Record<string, unknown>;
  // modificările APLICATE de bucla externă pe soluția asta, în cuvinte
  modificari_facute?: string[];
  // ce propune mai departe (payload-ul opac pleacă pe `job.output`)
  recomandari?: Record<string, unknown>[];
  recomandari_geometrie?: RecomandareGeometrie[];
  stuck_reason?: string | null;
  solver?: unknown;
  error?: string;
}

// O recomandare de geometrie a detaliului: ce ar fi schimbat solverul la
// geometria macro și n-a avut voie — textul omului, ținta și impactul estimat.
export interface RecomandareGeometrie {
  titlu?: string;             // ce propune, într-o frază
  ce?: string;                // ce s-ar schimba (pârghia, elementul, cât)
  de_ce?: string;             // verificarea picată care o cere
  impact?: number | null;     // câte picate ar atinge
  deficit_acoperit_pct?: number | null;
  cost_eur?: number | null;
  element?: string | null;
  verificare?: string;
  pas?: string;
  prioritate?: number | string;
  nr_picate?: number;
  eticheta?: string;
  cheie?: string;
  parghie?: string;
  ops?: unknown[];
  reglaje?: Record<string, unknown>;
}

// O SOLUȚIE de structură e un rând al registrului platformei, în forma
// contractului «soluțiile pe faze» — `nav/api.SolutieRand`, cu `faza`,
// `numar`, `structura.corpuri[].spatiu` (spațiul din care se citesc pașii) și
// `rulare`. Registrul propriu al structurii (`/structure/solutii`, cu
// `clone_id`, `favorita`, `fidelity`, `mod`) a dispărut (Î23).

// un PAS din istoricul de soluționare al Pasului 4 (`result.breakpoints`):
// starea geometriei după pasul determinist k, cerută cu `?bp=<k>` de la pasul
// însuși. NU e o soluție a proiectului — se oprește la Pasul 4 —, deci stă în
// tabelul de la finalul paginii Pasului 4.
export interface BreakpointPas4 {
  k: number; // numărul stării: pasul geometric, sau trecerea buclei macro
  stare: string; // gata | esuata
  livrata?: boolean; // starea cu care continuă soluția (★)
  eticheta?: string; // numele pasului („reancorarea grinzilor", „trecerea 2 (lean)")
  motiv?: string; // descrierea lui, din catalogul din model.py sau al buclei
  rezultat?: RezultatRamura | null;
  // geometria înghețată a stării: un rezultat de Pas 4 (subpașii proiectului,
  // se cere cu `?bp=k`) sau nimic (trecerile buclei macro își țin doar
  // geometria primară, sub `geometrie_primara`)
  model?: unknown | null;
}

// Rezumatul de o linie al progresului: „Iterația 3 · R2/R3/R4-geometrie ·
// model_fem (2/9) · 12,4 min". Folosit și în meniu, și în pagina pasului.
export function etapaTitlu(prg: {
  detaliu: ProgresDetaliu | null;
  pornit_la?: string;
}): string {
  const parti: string[] = [];
  const min =
    prg.pornit_la
      ? Math.round((Date.now() - new Date(prg.pornit_la).getTime()) / 60000)
      : null;
  if (min != null) parti.push(t("struct.etapa.minute", { n: min }));
  return parti.join(" · ");
}

export interface NivelInfo {
  fisier: string;
  nivel: string;
  suprafata_m2: number | null;
  sursa?: string; // "Pasul 2" când aria e preluată din analiza arhitecturii
  eroare?: string;
}

export interface FundatieResult {
  fisier: { id: number; name: string };
  motiv_fisier: string;
  regim_inaltime: string;
  nr_niveluri: number | null;
  motiv_niveluri: string;
  niveluri: NivelInfo[];
  suprafata_desfasurata_m2: number;
  /** de unde vine geometria pe care s-a așezat fundația. Rulările de dinainte
   * de poartă aici numele LAYERELOR de pe planșă; de atunci pasul
   *  nu mai deschide nicio planșă, iar geometria e cea măsurată în Pasul 2. */
  motiv_layere: string;
  layere_pereti?: string[];
  layere_goluri?: string[];
  lungime_fundatie_m: number;
  bbox_m: [number, number];
  nr_axe?: number;
  sursa_ax?: string;
  /** CE S-A ÎNDREPTAT la construirea axelor denumite: un ax e o dreaptă,
   *  iar grinzile din care se face merg din centru de stâlp în centru de
   *  stâlp, deci pot fi ușor înclinate. Lipsește pe un server mai vechi. */
  indreptare?: {
    nr_axe: number;
    pe_grila: number;
    abatere_max_m: number;
    rotire_max_grade: number;
    toleranta_offset_m: number;
    nota?: string;
  };
  error?: string;
}

